// Types for Stone data structure
// Using JSDoc for type documentation since this is a JS project

/**
 * @typedef {Object} Stone
 * @property {string} id - Unique identifier
 * @property {string} name - Display name of the stone
 * @property {string} sourceUrl - External URL for more information
 * @property {string[]} keywords - Associated keywords
 * @property {string} couleur - Color description
 * @property {string[]} chakras - Associated chakras
 * @property {string[]} signes - Associated zodiac signs
 * @property {string} bienfaits_emo_spirituels - Emotional/spiritual benefits description
 * @property {string} entretien - Care instructions
 * @property {string[]} needs - User needs this stone addresses
 * @property {string[]} families - Protection family types
 * @property {string[]} energyStyle - Energy style descriptors
 * @property {'douce'|'moyenne'|'forte'} intensity - Energy intensity level
 * @property {string[]} sensitivityFit - Sensitivity types it suits
 * @property {string[]} usageFits - Recommended usage contexts
 */

import stonesData from './stones.json';

/**
 * All available stones
 * @type {Stone[]}
 */
export const STONES = stonesData;

/**
 * Get a stone by its ID
 * @param {string} id 
 * @returns {Stone|undefined}
 */
export const getStoneById = (id) => {
  return STONES.find(stone => stone.id === id);
};

/**
 * Get stones by chakra
 * @param {string} chakra 
 * @returns {Stone[]}
 */
export const getStonesByChakra = (chakra) => {
  return STONES.filter(stone => 
    stone.chakras.some(c => c.toLowerCase().includes(chakra.toLowerCase()))
  );
};

/**
 * Get stones by need
 * @param {string} need 
 * @returns {Stone[]}
 */
export const getStonesByNeed = (need) => {
  return STONES.filter(stone => stone.needs.includes(need));
};

/**
 * Available needs for quiz
 */
export const AVAILABLE_NEEDS = [
  { id: 'stress', label: 'Réduire le stress et l\'anxiété', icon: 'wind' },
  { id: 'sommeil', label: 'Améliorer mon sommeil', icon: 'moon' },
  { id: 'protection', label: 'Me protéger des énergies négatives', icon: 'shield' },
  { id: 'confiance', label: 'Renforcer ma confiance en moi', icon: 'heart' },
  { id: 'ancrage', label: 'M\'ancrer et me stabiliser', icon: 'anchor' },
  { id: 'concentration', label: 'Améliorer ma concentration', icon: 'target' },
  { id: 'communication', label: 'Faciliter ma communication', icon: 'message-circle' },
  { id: 'intuition', label: 'Développer mon intuition', icon: 'eye' },
  { id: 'harmonie', label: 'Retrouver l\'harmonie intérieure', icon: 'heart-handshake' },
  { id: 'energie_positive', label: 'Attirer l\'énergie positive', icon: 'sun' }
];

/**
 * Protection types for quiz
 */
export const PROTECTION_TYPES = [
  { id: 'absorbante', label: 'Absorbante', description: 'Absorbe les énergies négatives' },
  { id: 'répulsive', label: 'Répulsive', description: 'Repousse et renvoie les énergies négatives' },
  { id: 'apaisante', label: 'Apaisante', description: 'Calme et harmonise les énergies' },
  { id: 'rayonnante', label: 'Rayonnante', description: 'Diffuse des énergies positives protectrices' }
];

/**
 * Sensitivity levels for quiz
 */
export const SENSITIVITY_LEVELS = [
  { id: 'tres_sensible', label: 'Très sensible', description: 'Je ressens facilement les énergies autour de moi' },
  { id: 'stable', label: 'Stable', description: 'Je suis généralement équilibré(e)' },
  { id: 'variable', label: 'Variable', description: 'Cela dépend des moments et des situations' }
];

/**
 * Intensity preferences for quiz
 */
export const INTENSITY_PREFERENCES = [
  { id: 'douce', label: 'Douce', description: 'Je préfère une énergie subtile et légère' },
  { id: 'moyenne', label: 'Moyenne', description: 'Je recherche un équilibre harmonieux' },
  { id: 'forte', label: 'Forte', description: 'Je suis prêt(e) pour une énergie puissante' }
];

/**
 * Usage contexts for quiz
 */
export const USAGE_CONTEXTS = [
  { id: 'sur_soi', label: 'À porter sur moi', description: 'En bijou ou dans ma poche' },
  { id: 'maison', label: 'Dans ma maison', description: 'Pour harmoniser mon espace de vie' },
  { id: 'meditation', label: 'Pour méditer', description: 'Pendant mes séances de méditation' },
  { id: 'table_de_nuit', label: 'Sur ma table de nuit', description: 'Pour accompagner mon sommeil' }
];

/**
 * Chakra options for quiz
 */
export const CHAKRA_OPTIONS = [
  { id: 'racine', label: 'Chakra Racine', color: 'chakra-root', description: 'Ancrage, sécurité, stabilité' },
  { id: 'sacre', label: 'Chakra Sacré', color: 'chakra-sacral', description: 'Créativité, émotions, plaisir' },
  { id: 'plexus', label: 'Chakra Plexus Solaire', color: 'chakra-plexus', description: 'Volonté, confiance, pouvoir personnel' },
  { id: 'coeur', label: 'Chakra du Cœur', color: 'chakra-heart', description: 'Amour, compassion, guérison' },
  { id: 'gorge', label: 'Chakra de la Gorge', color: 'chakra-throat', description: 'Communication, expression, vérité' },
  { id: 'troisieme_oeil', label: 'Chakra du Troisième Œil', color: 'chakra-third-eye', description: 'Intuition, clarté, vision' },
  { id: 'couronne', label: 'Chakra Couronne', color: 'chakra-crown', description: 'Spiritualité, conscience, sagesse' },
  { id: 'unknown', label: 'Je ne sais pas', color: 'muted', description: 'Pas de préférence particulière' }
];

export default STONES;
